import { useEffect, useState } from "react";
import { Alert, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import dayjs from "dayjs";
import EvidencePackageDialog from "./EvidencePackageDialog";
import { EVIDENCE_IN_PROGRESS_STATUSES, EVIDENCE_STATUS_LABELS, EvidenceDraftSummary, PackageFileKey, PackageProvider } from "../../types/evidence";
import { formatFileSize } from "../../utils/formatFileSize";
import { providerLabel } from "../../utils/textUtils";

// A save that failed left the server holding the old value. The field goes back to it and says so
// here, beside the control, rather than behind a toast that is gone in four seconds.
const ARN_SAVE_FAILED = "Not saved — this is the ARN the server holds. Enter it again.";

// Seconds while the wait is short, minutes once it is not: "320 s" is not a length anybody reads.
function waitedFor(since: string): string {
  const seconds = Math.max(dayjs.utc().diff(dayjs.utc(since), "second"), 0);
  return seconds < 90 ? `${seconds} s` : `${Math.floor(seconds / 60)} min`;
}

export default function EvidenceActions({
  draft, isStale, staleChanges, previewStale, trackingPending, inProgress, waitOverdue, pending, canDownload, fieldsUnsaved,
  onGenerate, onDownload, onStopWaiting, packageProvider, onDownloadPackageFile,
}: {
  draft: EvidenceDraftSummary;
  isStale: boolean;
  staleChanges: string[];
  /** The stored snapshot predates this build, so the document has to be generated again. */
  previewStale: boolean;
  trackingPending: boolean;
  /** The server owns the run, so every control on the dispute is closed until it is released. */
  inProgress: boolean;
  /** The wait is long past the minute the copy promises. */
  waitOverdue: boolean;
  pending: boolean;
  canDownload: boolean;
  /** A field holds something no blur can save (malformed, incomplete, or refused by the server), so generating would print the document without it. */
  fieldsUnsaved: boolean;
  onGenerate: () => void;
  onDownload: () => void;
  onStopWaiting: () => void;
  packageProvider: PackageProvider | null;
  onDownloadPackageFile: (file: PackageFileKey) => void;
}) {
  const [packageOpen, setPackageOpen] = useState(false);

  // A run the server will release on the next generate reads as failed here, so Generate reopens.
  const failed = draft.status === "failed" || (!inProgress && EVIDENCE_IN_PROGRESS_STATUSES.includes(draft.status));
  // What the run is doing, as the server last reported it.
  const stage = EVIDENCE_STATUS_LABELS[draft.status].toLowerCase();
  // A pending carrier is worth regenerating for, but only a PDF behind the preview closes the downloads.
  const needsUpdate = isStale || previewStale || trackingPending;
  // Never closed once generated: brand assets and policies change outside the draft, so only the
  // agent knows when the document should be printed again.
  const download = packageProvider && draft.package ? () => setPackageOpen(true) : onDownload;
  const label = inProgress
    ? "Generating…"
    : !draft.generated_at || failed ? "Generate evidence" : needsUpdate ? "Update evidence" : "Regenerate evidence";

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        {draft.generated_at && !inProgress && (
          <Typography variant="body2" color="text.secondary" component="div">
            <div>{`Generated ${dayjs(draft.generated_at).format("MMM D, HH:mm")}`}</div>
            {draft.pdf_bytes != null && (
              <div>
                {[
                  `PDF ${formatFileSize(draft.pdf_bytes)}`,
                  draft.pdf_pages != null && `${draft.pdf_pages} ${draft.pdf_pages === 1 ? "page" : "pages"}`,
                ].filter(Boolean).join(" · ")}
              </div>
            )}
          </Typography>
        )}

        {failed && <Alert severity="error">{draft.error_message || "Generation failed."}</Alert>}

        <Stack direction="row" spacing={1}>
          {/* Closed for as long as the server owns the run: a second generate would only be refused. */}
          <Button variant="contained" fullWidth onClick={onGenerate} disabled={inProgress || pending || fieldsUnsaved}>
            {label}
          </Button>
          <Button variant="outlined" fullWidth onClick={download} disabled={!canDownload || isStale}>
            Download
          </Button>
        </Stack>

        {packageProvider && (
          <>
            {canDownload && !draft.package && !inProgress && (
              <Typography variant="body2" color="text.secondary">Regenerate to get the split PDFs.</Typography>
            )}
            {draft.package && (draft.package.total.overLimit || draft.package.files.some((f) => f.overLimit)) && !inProgress && (
              <Alert severity="warning">{draft.package.total.overLimit ?? draft.package.files.find((f) => f.overLimit)!.overLimit}</Alert>
            )}
          </>
        )}

        {/* The preview beside the card is live; only the PDF file is behind, so the line says so
            under the button that serves it rather than above both as a verdict on the evidence. */}
        {isStale && !inProgress && (
          <Alert severity="warning">
            {`The PDF is behind the preview${staleChanges.length > 0 ? `: ${staleChanges.join(", ")}` : ""}. Update evidence to reprint it.`}
          </Alert>
        )}

        {inProgress && !waitOverdue && (
          <Typography variant="body2" color="text.secondary">
            {`Started ${waitedFor(draft.updated_at)} ago · usually under a minute`}
          </Typography>
        )}

        {/* The worker may be gone, and the alternative is a page nobody can touch for 15 minutes. */}
        {waitOverdue && (
          <Alert
            severity="warning"
            action={<Button color="inherit" size="small" onClick={onStopWaiting}>Stop waiting</Button>}
          >
            {`Still ${stage} after ${waitedFor(draft.updated_at)}. A generation usually takes under a minute.`}
          </Alert>
        )}
        {trackingPending && (
          <Typography variant="body2" color="text.secondary">Tracking still pending at carrier</Typography>
        )}
      </Stack>

      {packageProvider && draft.package && (
        <EvidencePackageDialog
          open={packageOpen}
          providerLabel={providerLabel(packageProvider)}
          pkg={draft.package}
          pdfBytes={draft.pdf_bytes}
          pdfPages={draft.pdf_pages}
          onDownload={onDownloadPackageFile}
          onDownloadPdf={onDownload}
          onClose={() => setPackageOpen(false)}
        />
      )}
    </Paper>
  );
}

export function CaseDetailsCard({ draft, inProgress, showArn, onSaveArn, onUnsavedChange }: {
  draft: EvidenceDraftSummary;
  inProgress: boolean;
  /** PayPal carries no ARN, so the field is hidden rather than shown empty; every card processor shows it. */
  showArn: boolean;
  /** Resolves false when the server did not take the value. */
  onSaveArn: (arn: string | null) => Promise<boolean>;
  /** The Generate button sits in another card, so the gate it needs is reported upward. */
  onUnsavedChange: (unsaved: boolean) => void;
}) {
  const [arn, setArn] = useState(draft.manual_arn ?? "");
  const [arnFailed, setArnFailed] = useState(false);
  // A newer save that landed after a failed one leaves the server holding what the field shows, so the
  // failure is over with the value it put back.
  useEffect(() => { setArn(draft.manual_arn ?? ""); setArnFailed(false); }, [draft.manual_arn]);
  // Digits for a Visa or Mastercard ARN, Stripe's RE_…_1 reference for an Amex refund; the API rejects
  // anything else, so a pasted sentence or hyphenated id is caught before it is sent.
  const arnInvalid = /[^A-Za-z0-9_]/.test(arn.trim());
  // A valid ARN still on screen is saved by the blur that clicking Generate causes, and the generation
  // waits for that save. Only what no blur can save closes the button: a malformed ARN, which is never
  // sent, and a save the server refused, which put the field back on the old value.
  const arnUnsaved = showArn && (arnInvalid || arnFailed);

  const saveArn = async () => {
    const value = arn.trim();
    if (arnInvalid || value === (draft.manual_arn ?? "")) return;
    setArnFailed(false);
    const saved = await onSaveArn(value || null);
    if (saved) return;
    setArn(draft.manual_arn ?? "");
    setArnFailed(true);
  };

  useEffect(() => { onUnsavedChange(arnUnsaved); }, [arnUnsaved, onUnsavedChange]);

  if (!showArn) return null;
  return (
    <TextField
      label="ARN (optional)"
      size="small"
      fullWidth
      value={arn}
      onChange={(event) => { setArn(event.target.value); setArnFailed(false); }}
      onBlur={saveArn}
      disabled={inProgress}
      error={arnInvalid || arnFailed}
      helperText={arnInvalid ? "Letters, digits and underscores only, up to 64" : arnFailed ? ARN_SAVE_FAILED : undefined}
      slotProps={{ htmlInput: { maxLength: 64, inputMode: "numeric" } }}
    />
  );
}
